import {LocalizationProvider} from '@mui/x-date-pickers';
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns';
import {useEffect} from 'react';
import {IntlProvider} from 'react-intl';
import {DelayedFullScreenLoading} from '../common/Loading';
import {startupProfiler} from '../common/utils/startupProfiler';
import {useLogger} from '../logging/LoggerProvider';
import {ComponentWithRequiredChildren, PropsWithRequiredChildren} from '../react/types';
import {useOptionalCurrentUser} from '../users/hooks/useOptionalCurrentUser';
import {usePreferredLocale} from '../users/hooks/usePreferredLocale';
import {LocaleContext} from './hooks/useCurrentLocale';
import {useI18n} from './hooks/useI18n';
import {LocaleWithCode, useLocale} from './hooks/useLocale';
import {extractLanguageFromLocale} from './locales';

interface L10nProviderProps extends PropsWithRequiredChildren {
  locale?: string; // locale can be specified manually for testing purposes
}

export const L10nProvider: ComponentWithRequiredChildren<L10nProviderProps> = ({
  children,
  locale,
}: L10nProviderProps) => {
  const maybeCurrentUser = useOptionalCurrentUser();

  if (locale) {
    return <I18nProviderWithOverride locale={locale}>{children}</I18nProviderWithOverride>;
  }

  if (maybeCurrentUser) {
    return <UserPreferredLocaleL10nProvider>{children}</UserPreferredLocaleL10nProvider>;
  }

  return <AutoDetectLocaleL10nProvider>{children}</AutoDetectLocaleL10nProvider>;
};

export const AutoDetectLocaleL10nProvider: ComponentWithRequiredChildren = ({children}: PropsWithRequiredChildren) => {
  return <InternalL10nProvider localeWithCode={useLocale()}>{children}</InternalL10nProvider>;
};

const UserPreferredLocaleL10nProvider: ComponentWithRequiredChildren = ({children}: PropsWithRequiredChildren) => {
  return <InternalL10nProvider localeWithCode={useLocale(usePreferredLocale())}>{children}</InternalL10nProvider>;
};

interface I18nProviderWithOverrideProps extends PropsWithRequiredChildren {
  locale: string;
}
const I18nProviderWithOverride: ComponentWithRequiredChildren<I18nProviderWithOverrideProps> = ({
  children,
  locale,
}: I18nProviderWithOverrideProps) => {
  return <InternalL10nProvider localeWithCode={useLocale(locale)}>{children}</InternalL10nProvider>;
};

interface InternalL10nProviderProps extends PropsWithRequiredChildren {
  localeWithCode?: LocaleWithCode;
}
const InternalL10nProvider: ComponentWithRequiredChildren<InternalL10nProviderProps> = ({
  children,
  localeWithCode,
}: InternalL10nProviderProps) => {
  const i18n = useI18n();
  const log = useLogger();
  useEffect(() => {
    async function doChangeLanguage() {
      // Do not change language from the render thread because this can cause react errors
      // about updating a component while rendering a different component
      try {
        await i18n.changeLanguage(localeLanguage);
      } catch (error) {
        /* v8 ignore next -- @preserve */
        log.error('Failed to change language', error);
      }
    }
    if (!localeWithCode) {
      return;
    }
    const localeLanguage = extractLanguageFromLocale(localeWithCode.code);
    if (i18n.resolvedLanguage !== localeLanguage) {
      void doChangeLanguage();
    }
  }, [i18n, localeWithCode, log]);

  useEffect(() => {
    if (localeWithCode) {
      startupProfiler.mark(`L10n Locale Ready (${localeWithCode.code})`);
    }
  }, [localeWithCode]);

  if (!localeWithCode) {
    return <DelayedFullScreenLoading />;
  }
  return (
    <LocaleContext value={{code: localeWithCode.code}}>
      <IntlProvider locale={localeWithCode.code}>
        <LocalizationProvider adapterLocale={localeWithCode.locale} dateAdapter={AdapterDateFns}>
          {children}
        </LocalizationProvider>
      </IntlProvider>
    </LocaleContext>
  );
};
